/* eslint-disable no-nested-ternary */
import React, { FC, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { Trans, useTranslation } from 'next-i18next';
import Image from 'next/legacy/image';

import { CCPO_FEATURES_V2 } from '@source/pages/CarroCertified/constants';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import CustomButton from '@source/components/CustomButton/CustomButton';
import CCPOFeaturesModalV2 from '@source/pages/CarroCertified/components/V2/CCPOFeaturesModalV2';
import Slider, { Settings } from 'react-slick';
import useMobileView from '@design-system/hooks/useMobileView';
import i18n from '@source/i18next';

import { IRegionProps, TRegionKey } from '@source/interface';
import CCPOMainContentTitleV2 from './CCPOMainContentTitleV2';

const StyledWrapper = styled.div`
  .control-btn {
    margin-top: 52px;
    padding-bottom: 35px;
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 16px;
  }

  .swipe-text {
    padding-bottom: 4px;
    font-size: 8px;
    line-height: 22px;
    font-style: italic;
    color: rgba(0, 0, 0, 0.7);
    text-align: right;
    font-weight: 200;
  }

  .slick-track {
    display: flex;
  }

  .slick-slide.slick-active {
    display: flex;
    justify-content: center;
  }

  @media screen and (max-width: 710px) {
    .slick-slide {
      margin: 0 5px;
      transform: translateX(-2px);
    }

    /* the parent */
    .slick-list {
      margin: 0 -5px;
    }
  }

  @media screen and (min-width: 711px) {
    .slick-track {
      gap: 15px;
    }

    .slick-slide.slick-active[aria-hidden='false'] {
      justify-content: end;
    }

    .slick-slide.slick-active[aria-hidden='false'] + .slick-slide.slick-active[aria-hidden='false'] {
      justify-content: start;
    }
  }

  @media screen and (min-width: 992px) {
    .slick-slider {
      padding-left: 100px;
      padding-right: 100px;
    }
  }
`;

const StyledButton = styled(CustomButton)`
  font-size: 12px;
  width: auto;
  color: ${(props) => props.theme.color.primaryBase};
  ${(props) => props.theme.typo.familyGoogle.semiBold}
  padding: 0;
  background-color: transparent;
  border: none !important;
  height: auto;
  justify-content: flex-start;

  @media screen and (min-width: 992px) {
    font-size: 14px;
  }

  &:hover {
    color: ${(props) => props.theme.color.primaryBase};
    background-color: transparent;
  }

  &:focus {
    background-color: transparent;
    color: ${(props) => props.theme.color.primaryBase};
    box-shadow: none;
    outline: none;
  }

  .anticon {
    line-height: 0;
  }
`;

const StyledCard = styled.div<{
  isBM?: boolean;
  isTwelveMonths?: boolean;
  cardContentTop?: string;
  isJP3rdCard?: boolean;
  isJP4thCard?: boolean;
}>`
  position: relative;
  width: 324px !important;
  height: 450px;
  border-radius: 12px;
  overflow: hidden;

  .card-content {
    width: 256px;
    position: absolute;
    top: 45%;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    flex-direction: column;
    align-items: center;

    .card-desc {
      margin-top: 20px;
      ${(props) => props.theme.typo.familyGoogle.regular}
      font-size: 9px;
      color: #fff;
      margin-bottom: ${(props) => (props.isBM ? '8px' : '0')};
      line-height: 15px;
      text-align: center;
      min-height: 120px;

      @media screen and (min-width: 1184px) {
        min-height: 100px;
      }
    }
  }

  @media screen and (min-width: 376px) {
    margin-right: 0;
  }

  ${(props) =>
    props.isJP3rdCard &&
    css`
      .card-content {
        top: 40%;
      }
    `}

  @media screen and (max-width: 991px) {
    ${(props) =>
      props.isJP4thCard &&
      css`
        .card-content {
          top: 40%;
        }
      `}
  }

  @media screen and (min-width: 1184px) {
    width: 475px !important;
    height: 650px;

    .card-content {
      width: ${(props) => (props.isJP3rdCard ? '397px' : '364px')};
      top: ${(props) => props.cardContentTop};

      .card-desc {
        margin-bottom: ${(props) => (props.isBM ? '28px' : '0')};
        font-size: 14px;
        line-height: 20px;
        min-height: 160px;
      }
    }
  }
`;

const StyledArrow = styled.div<{ isEnable?: boolean | undefined }>`
  display: block;
  width: 32px !important;
  height: 32px !important;
  text-align: center;
  cursor: ${(props) => (props.isEnable ? 'pointer' : 'not-allowed')};

  svg {
    background: ${(props) => (props.isEnable ? props.theme.color.primaryBase : '#A8A7A7')};
    color: ${(props) => props.theme.color.background};
    font-size: 32px;
    border-radius: 8px;
    padding: 8px;
  }
`;

type TFeatureModalProps = {
  modalTitle?: string;
  modalTitleMobile?: string;
  modalBody?: string;
};

type TFeatureLinkProps = {
  text: string;
};

type TCCPOFeatureProps = {
  key: string;
  title: string;
  titleMobile: string;
  description: string;
  descriptionMobile: string;
  image: string;
  modal?: TFeatureModalProps;
  link?: TFeatureLinkProps;
};

interface ArrowProps {
  className?: string;
  style?: React.CSSProperties;
  onClick?: React.MouseEventHandler<any>;
  isEnable?: boolean;
}

interface CCPOPageFeaturesV2Props {
  region: TRegionKey;
}

const PrevArrow: FC<ArrowProps> = ({ className, style, onClick, isEnable }) => (
  <StyledArrow isEnable={isEnable} className={`${className} slider-arrows`} style={style} onClick={onClick}>
    <LeftOutlined />
  </StyledArrow>
);

const NextArrow: FC<ArrowProps> = ({ className, style, onClick, isEnable }) => (
  <StyledArrow isEnable={isEnable} className={`${className} slider-arrows`} style={style} onClick={onClick}>
    <RightOutlined />
  </StyledArrow>
);

const CCPOPageFeaturesV2 = ({ region }: CCPOPageFeaturesV2Props) => {
  const sliderRef = useRef<Slider | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const isMobile = useMobileView(992);
  const isBM = i18n.language === 'ms';

  const [modalContent, setModalContent] = useState<TFeatureModalProps | null>(null);
  const [isModalOpen, setModalOpen] = useState(false);
  const content = CCPO_FEATURES_V2(region) || {};

  const { t } = useTranslation('ccpo');
  const onHandleLinkClick = (modal: TFeatureModalProps) => {
    setModalContent(modal);
    setModalOpen(true);
  };

  const settings: Settings = {
    infinite: false,
    dots: false,
    arrows: false,
    speed: 500,
    slidesToShow: 2,
    slidesToScroll: 1,
    responsive: [
      {
        breakpoint: 711,
        settings: {
          slidesToShow: 1,
        },
      },
    ],
    beforeChange: (oldIndex, newIndex) => {
      setActiveIndex(newIndex);
    },
  };

  const renderFeatures = () => (
    <>
      <Slider ref={sliderRef} {...settings}>
        {content.map(
          (
            { key, title, description, image, link, modal, titleMobile, descriptionMobile }: TCCPOFeatureProps,
            index: number,
          ) => {
            let cardContentTop = '48%';

            const isJP3rdCard = key === 'noMileage' && region === 'jp' && i18n.language !== 'ja';
            const isJP4thCard = key === 'noMajor' && region === 'jp' && i18n.language !== 'ja';

            if (isJP3rdCard && i18n.language !== 'ja') {
              cardContentTop = '43.5%';
            } else if (isBM) {
              if (key === 'twelveMonths') {
                cardContentTop = '40%';
              } else {
                cardContentTop = '44%';
              }
            }

            return (
              <StyledCard
                key={key}
                isBM={isBM}
                isTwelveMonths={key === 'twelveMonths'}
                cardContentTop={cardContentTop}
                isJP3rdCard={isJP3rdCard}
                isJP4thCard={isJP4thCard}
              >
                <Image
                  src={image.replace(
                    'https://cdn.carro.co/',
                    'https://cdn.carro.co/tr:n-carro_certified_page_vertical_background_image/',
                  )}
                  alt={key}
                  layout="fill"
                  objectFit="cover"
                  sizes="(max-width: 991px) 100vw, 475px"
                />
                <div className="card-content">
                  <CCPOMainContentTitleV2
                    isTextWhite
                    textNumber={`0${index + 2}`}
                    textTitle={t(isMobile ? titleMobile : title || '')}
                  />
                  <div className="card-desc">
                    <Trans ns="ccpo" defaults={t(isMobile ? descriptionMobile : description || '')} />
                  </div>
                  {link && modal && (
                    <StyledButton className="link-text" variant="text" onClick={() => onHandleLinkClick(modal)}>
                      {t(link?.text)} <RightOutlined style={{ fontSize: 16 }} />
                    </StyledButton>
                  )}
                </div>
              </StyledCard>
            );
          },
        )}
      </Slider>
      {isMobile ? (
        <div className="swipe-text">{t('ccpo.features.swipeToSeeMore')}</div>
      ) : (
        <div className="control-btn">
          <PrevArrow isEnable={activeIndex !== 0} onClick={() => sliderRef.current?.slickPrev()} />
          <NextArrow isEnable={activeIndex !== content.length - 2} onClick={() => sliderRef.current?.slickNext()} />
        </div>
      )}
    </>
  );

  return (
    <StyledWrapper>
      {renderFeatures()}
      <CCPOFeaturesModalV2
        title={
          <Trans ns="ccpo" defaults={t((isMobile ? modalContent?.modalTitleMobile : modalContent?.modalTitle) || '')} />
        }
        visible={isModalOpen}
        handleClose={() => setModalOpen(false)}
      >
        <Trans
          ns="ccpo"
          defaults={t(modalContent?.modalBody || '')}
          components={{ section: <section />, div: <div />, ul: <ul />, li: <li /> }}
        />
      </CCPOFeaturesModalV2>
    </StyledWrapper>
  );
};

export default CCPOPageFeaturesV2;
